import * as React from 'react'
import * as ReactRedux from 'react-redux'

import * as Constants from './constants'
import * as Player from './player'
import * as ServerConfiguration from './server-configuration'
import { useTransactionAmountLimits } from './use-transaction-amount-limits'

const MIN_LIMIT_BY_COUNTRY = {
  // 20€
  FI: 2000,
}

const MAX_LIMIT_BY_COUNTRY = {
  // 10 000€
  FI: 1000000,
}

function getFallbackLimitsByCountry(limits, countryCode) {
  return {
    max: MAX_LIMIT_BY_COUNTRY[countryCode] || limits.max,
    min: MIN_LIMIT_BY_COUNTRY[countryCode] || limits.min,
  }
}

/**
 * @param {'deposit'|'normal'} schemaId Schema ID
 */
export function useBriteDataSchema(schemaId) {
  const { limits } = useTransactionAmountLimits(
    Constants.TransactionType.DEPOSIT
  )
  const leftToDepositCents = ReactRedux.useSelector(state =>
    Player.getDepositLimitRemainder(state.player)
  )
  const playerCountryCode = ReactRedux.useSelector(state =>
    Player.getCountryCode(state.player)
  )
  const serverConfig = ServerConfiguration.useContext()

  const countryCode = playerCountryCode || serverConfig.country.alpha2

  const fallbackLimits = getFallbackLimitsByCountry(limits, countryCode)

  // TODO: Decouple "leaving contact details"-feature when
  // session updates appear seamlessly (currently its not
  // because of locale update in url on session update);
  // Move the `address`, and `email` out.
  const dataSchema = React.useMemo(() => {
    switch (schemaId) {
      case Constants.BriteLoginFlow.DEPOSIT: {
        return {
          type: 'object',
          properties: {
            amount: {
              maximum: Math.min(fallbackLimits.max, leftToDepositCents),
              minimum: fallbackLimits.min,
              type: 'number',
            },
            address: {
              countryCallingCode: {
                type: 'string',
              },
              mobile: {
                type: 'string',
              },
            },
            email: {
              type: 'string',
              pattern: Constants.EmailPattern,
            },
          },
          required: ['amount'],
        }
      }
      case Constants.BriteLoginFlow.NORMAL: {
        return {
          type: 'object',
          properties: {
            address: {
              countryCallingCode: {
                type: 'string',
              },
              mobile: {
                type: 'string',
              },
            },
            email: {
              type: 'string',
              pattern: Constants.EmailPattern,
            },
          },
        }
      }
      default: {
        throw new Error('Unknown schema id')
      }
    }
  }, [schemaId, fallbackLimits.min, fallbackLimits.max, leftToDepositCents])

  return dataSchema
}
